import styled from 'styled-components';
import { Link, Outlet, useLocation, useSearchParams } from 'react-router-dom';
import { useGetEconBdrStatusQuery } from '@shared/api';
import { riseIn } from '@shared/theme/animations';
import { IconArrowLeft } from '@shared/ui/icons';
import { ReadOnlyBanner } from '@entities/user';
import { ECON_MODULES, isEconModuleId, useEconPermission } from '@entities/economic/types';

const Wrap = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(7)};
  animation: ${riseIn} 420ms ${({ theme }) => theme.motion.easeOut};
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(4)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  min-height: 32px;
`;

const Back = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: none;
  padding: ${({ theme }) => theme.spacing(1)} ${({ theme }) => theme.spacing(2.5)};
  border-radius: ${({ theme }) => theme.radius.md};
  transition:
    background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  & > svg {
    transition: transform ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.spring};
  }

  &:hover > svg {
    transform: translateX(-3px);
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.02em;

  & .accent {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 800;
  }
`;

const SectionTitle = styled.h1`
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.01em;
`;

const Subtitle = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 15px;
  margin-top: ${({ theme }) => theme.spacing(2)};
  max-width: 760px;
`;

const QuickLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(4)};
`;

const QuickLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: none;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(4)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.pill};
  transition:
    border-color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderStrong};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

/**
 * Заголовок раздела по текущему пути `/agents/econ/<section>[/<id>]`.
 *  - корень → витрина модулей,
 *  - `runs` → список обработок (если в query есть ?module — уточняем модулем),
 *  - `runs/:id` → карточка обработки.
 */
function useSectionMeta(): { title: string; subtitle?: string } | null {
  const location = useLocation();
  const [params] = useSearchParams();
  const segments = location.pathname.split('/').filter(Boolean);
  // /agents/econ/<section>/<rest...>
  const section = segments[2];
  if (!section) return null;

  if (section === 'runs') {
    const hasId = Boolean(segments[3]);
    if (hasId) {
      return {
        title: 'Карточка обработки',
        subtitle:
          'Извлечённые данные, расчётные факты с план/фактом и отклонениями, выявленные аномалии. Трассировка любого факта — до исходной строки документа.',
      };
    }
    const moduleId = params.get('module');
    if (moduleId && isEconModuleId(moduleId)) {
      const m = ECON_MODULES[moduleId];
      return { title: m.title, subtitle: m.description };
    }
    return {
      title: 'Обработки отчётных периодов',
      subtitle: 'Загрузка документов, запуск расчёта и просмотр результатов по всем модулям.',
    };
  }

  // Хронум: часы людей и механизаторов по объектам, сопоставления, снимки.
  if (section === 'chronum') {
    return {
      title: 'Хронум',
      subtitle:
        'Часы людей и механизаторов по объектам — из Хронума. Здесь видно, что и когда загружено, и настраивается, куда идут часы: должность → категория ФОТ, площадка → стройка или центр затрат. Из этих снимков собирается БДР.',
    };
  }

  if (section === 'fleet') {
    return {
      title: 'Справочник техники',
      subtitle:
        'Паспорта моделей техники холдинга: что за машина, чья, сколько единиц, по каким нормам работает и сколько стоит. Из справочника берёт исходные данные расчёт ПРЦ машино-часа.',
    };
  }

  // Группы и ставки: средние ставки маш.-часа групп механизаторов.
  if (section === 'group-rates') {
    return {
      title: 'Ставки групп техники',
      subtitle:
        'Средняя ставка маш.-часа группы механизаторов на объекте. БДР умножает на неё часы механизаторов из Хронума — так считается стоимость техники без ручного сведения машинистов с машинами.',
    };
  }

  // ПРЦ машино-часа: список версий и версия (`prc/:id`).
  if (section === 'prc') {
    return segments[3]
      ? { title: 'ПРЦ машино-часа' }
      : {
          title: 'ПРЦ машино-часа',
          subtitle:
            'Планово-расчётная цена часа работы каждой модели техники: черновик расчёта, «было → стало», утверждение и запись цены в справочник техники. Формулы повторяют книгу «Калькуляция ЭММ» до копейки.',
        };
  }

  // БДР v2: месяц × объект из первичных источников.
  if (section === 'bdr') {
    return { title: 'БДР по объектам' };
  }

  // Приход ТМЦ отдела МТО: материалы БДР по объектам (созвон 03.11.2026).
  if (section === 'receipts') {
    return { title: 'Приход материалов' };
  }

  // Справочники: у каждой вкладки своё пояснение под вкладками.
  if (section === 'handbooks') {
    return { title: 'Справочники' };
  }

  if (section === 'plan-versions') {
    return { title: 'Версии плана' };
  }

  if (section === 'templates') {
    return {
      title: 'Шаблоны отчётов',
      subtitle:
        'XLSX-шаблоны, по которым собираются итоговые отчёты обработок. Версионирование по модулям: загрузка новых версий, выбор активной и скачивание.',
    };
  }

  return { title: section };
}

export function EconLayout() {
  const location = useLocation();
  const segments = location.pathname.split('/').filter(Boolean);
  const isRoot = segments.length === 2; // /agents/econ
  // Одна стрелка «назад» — к разделу на уровень выше: со страницы версии ПРЦ —
  // к списку версий, а не на витрину Экономиста (раньше их было две, и на
  // созвоне 29.10.2026 по верхней уходили на витрину).
  const back =
    segments[2] === 'prc' && segments.length > 3
      ? { to: '/agents/econ/prc', label: 'ПРЦ машино-часа' }
      : { to: '/agents/econ', label: 'ОСНОВА.Экономист' };
  const meta = useSectionMeta();
  const { can_read, can_write } = useEconPermission();
  // Ссылка на БДР v2 — только когда он включён (флаг OSNOVA_ECON_V2_ENABLED):
  // до сверки проверочного месяца экран не показываем.
  const { data: bdrStatus } = useGetEconBdrStatusQuery(undefined, { skip: !can_read });

  return (
    <Wrap>
      <Header>
        {isRoot ? (
          <>
            <Title>
              ОСНОВА<span className="accent">.Экономист</span>
            </Title>
            <Subtitle>
              Планово-экономический отдел: управленческая отчётность по пяти направлениям — бюджет
              доходов и расходов, добыча полезных ископаемых, столовая, бюджет движения денежных
              средств и консолидированный пакет для руководства. Арифметику считает
              детерминированный движок, каждый факт прослеживается до исходного документа.
            </Subtitle>
            <QuickLinks>
              <QuickLink to="/agents/econ/templates">Шаблоны отчётов</QuickLink>
              <QuickLink to="/agents/econ/handbooks">Справочники</QuickLink>
              {/* Хронум */}
              <QuickLink to="/agents/econ/chronum">Хронум</QuickLink>
              {/* БДР v2 — месяц × объект из первичных источников */}
              {bdrStatus?.enabled && <QuickLink to="/agents/econ/bdr">БДР по объектам</QuickLink>}
              {/* Приход ТМЦ отдела МТО — материалы БДР по объектам */}
              <QuickLink to="/agents/econ/receipts">Приход материалов</QuickLink>
              <QuickLink to="/agents/econ/fleet">Техника</QuickLink>
              {/* ПРЦ машино-часа */}
              <QuickLink to="/agents/econ/prc">ПРЦ машино-часа</QuickLink>
              <QuickLink to="/agents/econ/plan-versions">Версии плана</QuickLink>
              {/* Группы и ставки */}
              <QuickLink to="/agents/econ/group-rates">Ставки групп</QuickLink>
              <QuickLink to="/agents/finance">ОСНОВА.Финансист →</QuickLink>
            </QuickLinks>
          </>
        ) : (
          <>
            <TopRow>
              <Back to={back.to}>
                <IconArrowLeft size={16} />
                <span>{back.label}</span>
              </Back>
            </TopRow>
            <SectionTitle>{meta?.title}</SectionTitle>
            {meta?.subtitle && <Subtitle>{meta.subtitle}</Subtitle>}
          </>
        )}
      </Header>
      {can_read && !can_write && <ReadOnlyBanner />}
      <Outlet />
    </Wrap>
  );
}
